import { Component, OnInit, OnDestroy, ChangeDetectorRef, NgZone, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import {
  NgbPaginationModule,
  NgbTooltipModule,
  NgbAlertModule,
  NgbDropdownModule,
  NgbModal
} from '@ng-bootstrap/ng-bootstrap';

import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { SafePipe } from '../../pipes/safe.pipe';
import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { environment } from '../../../environments/environment';
import { csvFileDateSuffix, downloadCsv } from '../../utils/csv.util';

/** ประเภทรายการ = 1 แท็บ ('all' คือแท็บรวมทั้งหมด) */
export type PaymentSourceType =
  | 'member-card'
  | 'renew-member'
  | 'register-appraiser'
  | 'renew-appraiser'
  | 'training'
  | 'exam';

export type PaymentTabId = 'all' | PaymentSourceType;

/** แถวข้อมูลรูปแบบเดียวกันทุกประเภท (ตรงกับ API /payment-verification) */
export interface PaymentVerificationRow {
  sourceType: PaymentSourceType;
  id: string;
  submittedAt: string | null;
  fullName: string;
  email: string;
  userId: string | null;
  /** เลขบัตรประชาชน (ใช้ในหน้ารายละเอียดกลาง) */
  cid: string;
  /** เบอร์โทรติดต่อ */
  phoneNumber: string;
  /** สถานะบัญชีผู้ใช้ */
  accountActive: boolean;
  memberNo: string;
  organization: string;
  itemTitle: string;
  amount: number;
  paymentStatus: string;
  transferDateTime: string | null;
  paymentSlip: string | null;
  codeReceipt: string | null;
  receiptType: string | null;
  receiptLogId: string | null;
}

export interface PaymentVerificationFilters {
  searchTerm: string;
  email: string;
  codeReceipt: string;
  itemTitle: string;
  paymentStatus: string;
  amountMin: string;
  amountMax: string;
  dateFrom: string;
  dateTo: string;
}

/** สถานะที่ใช้กับใบคำขอ (เก็บเป็นข้อความ) */
const FORM_PAYMENT_STATUSES = [
  'รอชำระเงิน',
  'รอตรวจสอบยอดชำระ',
  'ชำระเงินเสร็จสิ้น',
  'ไม่ผ่านการตรวจสอบ',
  'ยกเลิกการชำระ'
];

/** การอบรม/สมัครสอบเก็บสถานะเป็นตัวเลข จึงมีให้เลือกน้อยกว่า */
const NUMERIC_PAYMENT_STATUSES = [
  'รอชำระเงิน',
  'รอตรวจสอบยอดชำระ',
  'ชำระเงินเสร็จสิ้น',
  'ยกเลิกการชำระ'
];

@Component({
  selector: 'app-payment-verification',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbPaginationModule,
    NgbTooltipModule,
    NgbAlertModule,
    NgbDropdownModule,
    StaffHeaderComponent,
    StaffNavComponent,
    SafePipe
  ],
  templateUrl: './payment-verification.component.html',
  styleUrl: './payment-verification.component.scss'
})
export class PaymentVerificationComponent implements OnInit, OnDestroy {
  @ViewChild('statusModal') statusModal!: TemplateRef<any>;
  @ViewChild('receiptModal') receiptModal!: TemplateRef<any>;

  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;

  /** 7 แท็บ = รวมทั้งหมด + 6 ประเภทใบคำขอ */
  tabs: { id: PaymentTabId; label: string }[] = [
    { id: 'all', label: 'รวมทั้งหมด' },
    { id: 'member-card', label: 'ขอทำบัตรสมาชิก' },
    { id: 'renew-member', label: 'ขอต่ออายุสมาชิก' },
    { id: 'register-appraiser', label: 'ขอขึ้นทะเบียน' },
    { id: 'renew-appraiser', label: 'ขอต่ออายุบัตร' },
    { id: 'training', label: 'การอบรม' },
    { id: 'exam', label: 'การสมัครสอบ' }
  ];
  activeTab: PaymentTabId = 'all';

  records: PaymentVerificationRow[] = [];
  filteredRecords: PaymentVerificationRow[] = [];
  isLoading = false;
  error: string | null = null;

  filters: PaymentVerificationFilters = {
    searchTerm: '',
    email: '',
    codeReceipt: '',
    itemTitle: '',
    paymentStatus: '',
    amountMin: '',
    amountMax: '',
    dateFrom: '',
    dateTo: ''
  };

  /** ตัวเลือกสถานะในตัวกรอง (รวมทุกสถานะที่เป็นไปได้) */
  readonly allPaymentStatuses = FORM_PAYMENT_STATUSES;

  // แบ่งหน้า (กรอง/แบ่งหน้าฝั่ง client เหมือนหน้าการจัดการทางบัญชี)
  page = 1;
  pageSize = 10;
  readonly pageSizeOptions = [10, 20, 50, 100];
  collectionSize = 0;

  // เรียงลำดับ
  sortField = 'submittedAt';
  sortDirection: 'asc' | 'desc' = 'desc';

  // หน้าต่างปรับสถานะการชำระเงิน
  statusTarget: PaymentVerificationRow | null = null;
  statusValue = '';
  statusCancelReason = '';
  isSavingStatus = false;

  // หน้าต่างแก้ไขใบเสร็จ
  receiptTarget: PaymentVerificationRow | null = null;
  receiptTransferDateTime = '';
  receiptTypeValue: 'individual' | 'company' = 'individual';
  isSavingReceipt = false;

  // หลักฐานการชำระเงิน (ย่อ-ขยายได้)
  showSlipPreview = false;
  slipPreviewUrl: string | null = null;
  slipPreviewIsPdf = false;
  slipZoom = 1;
  slipOffsetX = 0;
  slipOffsetY = 0;
  private slipDragging = false;
  private slipDragStartX = 0;
  private slipDragStartY = 0;
  private readonly SLIP_ZOOM_MIN = 0.25;
  private readonly SLIP_ZOOM_MAX = 5;
  private readonly SLIP_ZOOM_STEP = 0.25;

  private subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
        this.cdr.detectChanges();
      })
    );

    this.restoreViewStateFromUrl();
    this.loadRecords();
  }

  /**
   * แท็บและหน้าที่เปิดอยู่ถูกเก็บไว้บน URL
   *
   * เพื่อให้กลับมาจากหน้าใบเสร็จ (ปุ่มย้อนกลับส่ง returnUrl กลับมา) แล้วยังอยู่
   * แท็บเดิมหน้าเดิม จะได้พิมพ์ใบเสร็จรายถัดไปต่อได้เลย ไม่ต้องไล่เลือกแท็บใหม่
   */
  private restoreViewStateFromUrl(): void {
    const params = this.route.snapshot.queryParamMap;

    const tab = params.get('tab') as PaymentTabId | null;
    if (tab && this.tabs.some(t => t.id === tab)) {
      this.activeTab = tab;
    }

    const page = Number(params.get('page'));
    if (Number.isInteger(page) && page > 0) {
      this.page = page;
    }

    const pageSize = Number(params.get('pageSize'));
    if (this.pageSizeOptions.includes(pageSize)) {
      this.pageSize = pageSize;
    }
  }

  /** เขียนแท็บ/หน้าปัจจุบันลง URL (แทนที่ประวัติเดิม ไม่เพิ่มรายการย้อนกลับ) */
  private syncViewStateToUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        tab: this.activeTab,
        page: this.page,
        pageSize: this.pageSize
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();
  }

  // ── โหลดข้อมูล ──────────────────────────────────────────────────────────

  loadRecords(): void {
    this.isLoading = true;
    this.error = null;
    this.cdr.detectChanges();

    const typeParam = this.activeTab === 'all' ? '' : `?type=${this.activeTab}`;
    const apiUrl = `${environment.apiUrl}/payment-verification${typeParam}`;

    const sub = this.http.get<{ data: PaymentVerificationRow[]; total: number }>(apiUrl).subscribe({
      next: response => {
        this.ngZone.run(() => {
          this.records = response?.data || [];
          this.applyFilters();
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.ngZone.run(() => {
          console.error('❌ โหลดรายการชำระเงินไม่สำเร็จ:', err);
          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${err.message}`;
          this.records = [];
          this.filteredRecords = [];
          this.collectionSize = 0;
          this.isLoading = false;
          this.cdr.detectChanges();
        });
      }
    });

    this.subscription.add(sub);
  }

  refreshData(): void {
    this.loadRecords();
  }

  switchTab(tabId: PaymentTabId): void {
    if (this.activeTab === tabId) return;
    this.activeTab = tabId;
    this.page = 1;
    this.syncViewStateToUrl();
    this.loadRecords();
  }

  getTabCount(tabId: PaymentTabId): number {
    if (tabId !== this.activeTab) return 0;
    return this.filteredRecords.length;
  }

  // ── ตัวกรอง / เรียงลำดับ / แบ่งหน้า ──────────────────────────────────────

  applyFilters(): void {
    const matched = this.records.filter(record => this.recordMatchesFilters(record));
    this.filteredRecords = this.sortRecords(matched);
    this.collectionSize = this.filteredRecords.length;
    if ((this.page - 1) * this.pageSize >= this.collectionSize) {
      this.page = 1;
    }
    this.cdr.detectChanges();
  }

  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      email: '',
      codeReceipt: '',
      itemTitle: '',
      paymentStatus: '',
      amountMin: '',
      amountMax: '',
      dateFrom: '',
      dateTo: ''
    };
    this.page = 1;
    this.applyFilters();
  }

  private recordMatchesFilters(record: PaymentVerificationRow): boolean {
    const f = this.filters;

    if (f.searchTerm) {
      const term = f.searchTerm.toLowerCase();
      const haystack = [
        record.fullName,
        record.email,
        record.memberNo,
        record.organization,
        record.itemTitle,
        record.codeReceipt
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(term)) return false;
    }

    if (f.email && !(record.email || '').toLowerCase().includes(f.email.toLowerCase())) {
      return false;
    }

    if (f.codeReceipt && !(record.codeReceipt || '').toLowerCase().includes(f.codeReceipt.toLowerCase())) {
      return false;
    }

    if (f.itemTitle && !(record.itemTitle || '').toLowerCase().includes(f.itemTitle.toLowerCase())) {
      return false;
    }

    if (f.paymentStatus && record.paymentStatus !== f.paymentStatus) {
      return false;
    }

    const amountMin = parseFloat(f.amountMin);
    if (!isNaN(amountMin) && record.amount < amountMin) return false;

    const amountMax = parseFloat(f.amountMax);
    if (!isNaN(amountMax) && record.amount > amountMax) return false;

    // ช่วงวันที่กรองจากวันที่ทำรายการในระบบ
    if (f.dateFrom || f.dateTo) {
      if (!record.submittedAt) return false;
      const submitted = new Date(record.submittedAt);
      if (isNaN(submitted.getTime())) return false;

      if (f.dateFrom && submitted < new Date(`${f.dateFrom}T00:00:00`)) return false;
      if (f.dateTo && submitted > new Date(`${f.dateTo}T23:59:59`)) return false;
    }

    return true;
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  isSortedBy(field: string): boolean {
    return this.sortField === field;
  }

  getSortIcon(field: string): string {
    if (!this.isSortedBy(field)) return '↕';
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  private sortRecords(records: PaymentVerificationRow[]): PaymentVerificationRow[] {
    if (!this.sortField) return records;
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    return [...records].sort((a, b) => {
      const av = this.getSortValue(a, this.sortField);
      const bv = this.getSortValue(b, this.sortField);
      if (av === bv) return 0;
      if (av === null || av === undefined || av === '') return 1;
      if (bv === null || bv === undefined || bv === '') return -1;
      return (av > bv ? 1 : -1) * direction;
    });
  }

  private getSortValue(record: PaymentVerificationRow, field: string): any {
    switch (field) {
      case 'submittedAt': return record.submittedAt ? new Date(record.submittedAt).getTime() : 0;
      case 'name': return record.fullName;
      case 'memberNo': return record.memberNo;
      case 'organization': return record.organization;
      case 'itemTitle': return record.itemTitle;
      case 'amount': return record.amount;
      case 'paymentStatus': return record.paymentStatus;
      case 'paidAt': return record.transferDateTime || '';
      case 'paymentSlip': return record.paymentSlip ? 1 : 0;
      case 'codeReceipt': return record.codeReceipt || '';
      default: return (record as any)[field];
    }
  }

  getCurrentPageRecords(): PaymentVerificationRow[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredRecords.slice(start, start + this.pageSize);
  }

  onPageChange(newPage: number): void {
    this.page = newPage;
    this.syncViewStateToUrl();
    this.cdr.detectChanges();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    this.page = 1;
    this.syncViewStateToUrl();
    this.cdr.detectChanges();
  }

  trackByRecordId(index: number, record: PaymentVerificationRow): string {
    return `${record.sourceType}:${record.id}`;
  }

  min(a: number, b: number): number {
    return Math.min(a, b);
  }

  // ── การแสดงผลแต่ละคอลัมน์ ────────────────────────────────────────────────

  formatDateTime(value?: string | null): string {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';
    return `${date.toLocaleDateString('th-TH')} ${date.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.`;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  }

  /** วันที่และเวลาชำระเงิน — API ส่ง transferDateTime มาเป็นข้อความไทยอยู่แล้ว */
  getPaidAt(record: PaymentVerificationRow): string {
    return record.transferDateTime || '-';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'ชำระเงินเสร็จสิ้น': return 'bg-success';
      case 'รอตรวจสอบยอดชำระ': return 'bg-warning text-dark';
      case 'รอชำระเงิน': return 'bg-secondary';
      case 'ไม่ผ่านการตรวจสอบ': return 'bg-danger';
      case 'ยกเลิกการชำระ': return 'bg-dark';
      default: return 'bg-light text-dark';
    }
  }

  getTypeLabel(sourceType: PaymentSourceType): string {
    return this.tabs.find(tab => tab.id === sourceType)?.label || sourceType;
  }

  getReceiptTypeLabel(receiptType: string | null): string {
    if (receiptType === 'company') return 'นิติบุคคล';
    if (receiptType === 'individual') return 'บุคคลธรรมดา';
    return '-';
  }

  // ── ลิงก์ไปหน้าอื่น ──────────────────────────────────────────────────────

  /** ใบคำขอของรายการนี้ — หน้าเดียวกับที่เจ้าหน้าที่ตรวจเอกสารใช้ */
  getDetailRoute(record: PaymentVerificationRow): string[] {
    switch (record.sourceType) {
      case 'member-card': return ['/staff/request-regis-new-member-form', record.id];
      case 'renew-member': return ['/staff/request-renew-member-form', record.id];
      case 'register-appraiser': return ['/staff/request-regis-new-appraiser-form', record.id];
      case 'renew-appraiser': return ['/staff/request-renew-appraiser-form', record.id];
      case 'training': return ['/staff/cpd-hours-management/detail', record.id];
      case 'exam': return ['/staff/exam-list-management/detail', record.id];
      default: return ['/staff/payment-verification'];
    }
  }

  /** คลิกที่ชื่อผู้ทำรายการ -> ใบคำขอ (หน้าเดียวกับที่เจ้าหน้าที่ตรวจเอกสารใช้) */
  viewRequestDetail(record: PaymentVerificationRow): void {
    this.router.navigate(this.getDetailRoute(record), {
      queryParams: { returnUrl: this.router.url }
    });
  }

  /** Action > ดูรายละเอียด -> หน้ารายละเอียดกลางของการตรวจสอบการชำระเงิน */
  viewPaymentDetail(record: PaymentVerificationRow): void {
    this.router.navigate(
      ['/staff/payment-verification/detail', record.sourceType, record.id],
      { queryParams: { returnUrl: this.router.url } }
    );
  }

  hasReceipt(record: PaymentVerificationRow): boolean {
    return !!record.codeReceipt;
  }

  /**
   * เปิดหน้าใบเสร็จเพื่อสั่งพิมพ์
   * การสมัครสอบใช้ id ของ receipt_log เป็นพารามิเตอร์หลัก ส่วนประเภทอื่นใช้ id ของต้นทาง
   */
  printReceipt(record: PaymentVerificationRow): void {
    if (!this.hasReceipt(record)) {
      alert('รายการนี้ยังไม่ได้ออกใบเสร็จ');
      return;
    }

    if (record.sourceType === 'exam') {
      if (!record.receiptLogId) {
        alert('ไม่พบข้อมูลใบเสร็จของรายการนี้');
        return;
      }
      this.router.navigate(['/staff/renew-slip', record.receiptLogId], {
        queryParams: { type: 'exam', returnUrl: this.router.url }
      });
      return;
    }

    this.router.navigate(['/staff/renew-slip', record.id], {
      queryParams: {
        type: record.sourceType,
        receiptLogId: record.receiptLogId || undefined,
        returnUrl: this.router.url
      }
    });
  }

  // ── ปรับสถานะการชำระเงิน ─────────────────────────────────────────────────

  /** สถานะที่เลือกได้ ขึ้นกับประเภทรายการ (อบรม/สมัครสอบเก็บเป็นตัวเลข) */
  getStatusOptions(record: PaymentVerificationRow | null): string[] {
    if (!record) return FORM_PAYMENT_STATUSES;
    return record.sourceType === 'training' || record.sourceType === 'exam'
      ? NUMERIC_PAYMENT_STATUSES
      : FORM_PAYMENT_STATUSES;
  }

  openStatusDialog(record: PaymentVerificationRow): void {
    this.statusTarget = record;
    this.statusValue = this.getStatusOptions(record).includes(record.paymentStatus)
      ? record.paymentStatus
      : this.getStatusOptions(record)[0];
    this.statusCancelReason = '';
    this.isSavingStatus = false;
    this.modalService.open(this.statusModal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  get isCancelingPayment(): boolean {
    return this.statusValue === 'ยกเลิกการชำระ';
  }

  get willIssueReceipt(): boolean {
    return this.statusValue === 'ชำระเงินเสร็จสิ้น' && !!this.statusTarget && !this.statusTarget.codeReceipt;
  }

  /** แอปรันแบบ zoneless จึงต้องสั่ง render ใหม่เองหลังเปลี่ยนค่าใน dropdown */
  onStatusSelectionChanged(): void {
    this.cdr.detectChanges();
  }

  savePaymentStatus(modal: any): void {
    if (!this.statusTarget) return;

    const record = this.statusTarget;
    const body: any = { paymentStatus: this.statusValue };
    if (this.isCancelingPayment && this.statusCancelReason.trim()) {
      body.cancelReason = this.statusCancelReason.trim();
    }

    this.isSavingStatus = true;
    this.cdr.detectChanges();

    const apiUrl = `${environment.apiUrl}/payment-verification/${record.sourceType}/${record.id}/payment-status`;
    this.http.patch<PaymentVerificationRow>(apiUrl, body).subscribe({
      next: updated => {
        this.ngZone.run(() => {
          this.isSavingStatus = false;
          this.applyUpdatedRow(record, updated);
          modal.close();
          const issued = updated?.codeReceipt && !record.codeReceipt;
          alert(issued
            ? `บันทึกสถานะเรียบร้อย และออกใบเสร็จเลขที่ ${updated.codeReceipt} แล้ว`
            : 'บันทึกสถานะการชำระเงินเรียบร้อยแล้ว');
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.ngZone.run(() => {
          console.error('❌ ปรับสถานะการชำระเงินไม่สำเร็จ:', err);
          this.isSavingStatus = false;
          alert(`ปรับสถานะไม่สำเร็จ: ${err.error?.message || err.message}`);
          this.cdr.detectChanges();
        });
      }
    });
  }

  // ── แก้ไขใบเสร็จ (วันเวลาโอน / ประเภทใบเสร็จ) ────────────────────────────

  openReceiptDialog(record: PaymentVerificationRow): void {
    this.receiptTarget = record;
    this.receiptTransferDateTime = record.transferDateTime || '';
    this.receiptTypeValue = record.receiptType === 'company' ? 'company' : 'individual';
    this.isSavingReceipt = false;
    this.modalService.open(this.receiptModal, { size: 'lg', centered: true, backdrop: 'static' });
  }

  saveReceiptInfo(modal: any): void {
    if (!this.receiptTarget) return;

    const record = this.receiptTarget;
    const body: any = {
      transferDateTime: this.receiptTransferDateTime.trim(),
      receiptType: this.receiptTypeValue
    };

    this.isSavingReceipt = true;
    this.cdr.detectChanges();

    const apiUrl = `${environment.apiUrl}/payment-verification/${record.sourceType}/${record.id}/receipt`;
    this.http.patch<PaymentVerificationRow>(apiUrl, body).subscribe({
      next: updated => {
        this.ngZone.run(() => {
          this.isSavingReceipt = false;
          this.applyUpdatedRow(record, updated);
          modal.close();
          alert('บันทึกข้อมูลใบเสร็จเรียบร้อยแล้ว');
          this.cdr.detectChanges();
        });
      },
      error: (err: HttpErrorResponse) => {
        this.ngZone.run(() => {
          console.error('❌ แก้ไขใบเสร็จไม่สำเร็จ:', err);
          this.isSavingReceipt = false;
          alert(`แก้ไขใบเสร็จไม่สำเร็จ: ${err.error?.message || err.message}`);
          this.cdr.detectChanges();
        });
      }
    });
  }

  /** อัปเดตแถวเดิมในตารางด้วยค่าที่ API ส่งกลับ (ไม่ต้องโหลดใหม่ทั้งหน้า) */
  private applyUpdatedRow(target: PaymentVerificationRow, updated: PaymentVerificationRow | null): void {
    if (!updated) {
      this.loadRecords();
      return;
    }
    const index = this.records.findIndex(
      row => row.sourceType === target.sourceType && row.id === target.id
    );
    if (index >= 0) {
      this.records[index] = updated;
    }
    this.applyFilters();
  }

  // ── Export CSV ───────────────────────────────────────────────────────────

  exportToCSV(): void {
    if (this.filteredRecords.length === 0) {
      alert('ไม่พบข้อมูลที่ตรงกับเงื่อนไขการกรอง');
      return;
    }

    const headers = [
      'ประเภทรายการ',
      'วันและเวลาที่ทำรายการ',
      'ชื่อ-นามสกุล',
      'อีเมล',
      'เลขที่สมาชิกสมาคม',
      'บริษัท/หน่วยงาน',
      'ชื่อรายการ',
      'จำนวนเงิน',
      'สถานะการชำระเงิน',
      'วันที่และเวลาชำระเงิน',
      'หลักฐานการชำระเงิน',
      'เลขที่ใบเสร็จ',
      'ประเภทใบเสร็จ'
    ];

    const rows = this.filteredRecords.map(record => [
      this.getTypeLabel(record.sourceType),
      this.formatDateTime(record.submittedAt),
      record.fullName || '-',
      record.email || '-',
      record.memberNo || '-',
      record.organization || '-',
      record.itemTitle || '-',
      record.amount,
      record.paymentStatus || '-',
      this.getPaidAt(record),
      record.paymentSlip ? 'มี' : 'ไม่มี',
      record.codeReceipt || '-',
      this.getReceiptTypeLabel(record.receiptType)
    ]);

    downloadCsv(`payment-verification_${this.activeTab}_${csvFileDateSuffix()}`, headers, rows);
  }

  // ── หน้าต่างดูหลักฐานการชำระเงิน (ย่อ-ขยายได้) ──────────────────────────

  openSlipPreview(url: string): void {
    this.slipPreviewUrl = url;
    this.slipPreviewIsPdf = /\.pdf(\?|#|$)/i.test(url);
    this.resetSlipZoom();
    this.showSlipPreview = true;
    this.cdr.detectChanges();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.showSlipPreview) {
      this.closeSlipPreview();
    }
  }

  closeSlipPreview(): void {
    this.showSlipPreview = false;
    this.slipPreviewUrl = null;
    this.slipDragging = false;
    this.resetSlipZoom();
    this.cdr.detectChanges();
  }

  zoomSlipIn(): void {
    this.setSlipZoom(this.slipZoom + this.SLIP_ZOOM_STEP);
  }

  zoomSlipOut(): void {
    this.setSlipZoom(this.slipZoom - this.SLIP_ZOOM_STEP);
  }

  resetSlipZoom(): void {
    this.slipZoom = 1;
    this.slipOffsetX = 0;
    this.slipOffsetY = 0;
    this.cdr.detectChanges();
  }

  private setSlipZoom(value: number): void {
    const next = Math.min(this.SLIP_ZOOM_MAX, Math.max(this.SLIP_ZOOM_MIN, Math.round(value * 100) / 100));
    if (next === this.slipZoom) return;
    this.slipZoom = next;
    if (next <= 1) {
      this.slipOffsetX = 0;
      this.slipOffsetY = 0;
    }
    this.cdr.detectChanges();
  }

  get slipZoomPercent(): number {
    return Math.round(this.slipZoom * 100);
  }

  onSlipWheel(event: WheelEvent): void {
    event.preventDefault();
    this.setSlipZoom(this.slipZoom + (event.deltaY < 0 ? this.SLIP_ZOOM_STEP : -this.SLIP_ZOOM_STEP));
  }

  onSlipDragStart(event: MouseEvent): void {
    if (this.slipZoom <= 1) return;
    event.preventDefault();
    this.slipDragging = true;
    this.slipDragStartX = event.clientX - this.slipOffsetX;
    this.slipDragStartY = event.clientY - this.slipOffsetY;
  }

  onSlipDragMove(event: MouseEvent): void {
    if (!this.slipDragging) return;
    event.preventDefault();
    this.slipOffsetX = event.clientX - this.slipDragStartX;
    this.slipOffsetY = event.clientY - this.slipDragStartY;
    this.cdr.detectChanges();
  }

  onSlipDragEnd(): void {
    this.slipDragging = false;
  }

  openSlipInNewTab(): void {
    if (this.slipPreviewUrl) {
      window.open(this.slipPreviewUrl, '_blank');
    }
  }
}
